import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../../../services/api';
import config from '../../../../../config';
import { encodePathSegments } from '../../../../../utils/media-url';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Switch,
    TextField,
    Typography,
    useTheme
} from '@mui/material';

import {
    IconSearch,
    IconPlus,
    IconDeviceFloppy,
    IconTrash,
    IconX,
    IconMessageCircle,
    IconBrandWhatsapp,
    IconBrandInstagram,
    IconBrandMessenger,
    IconPlugConnected,
    IconChevronRight,
    IconRefresh,
    IconWorldWww,
    IconRobot,
    IconSettings,
    IconMail,
    IconPhoneCall,
    IconMessages,
    IconArrowLeft,
    IconCalendarEvent
} from '@tabler/icons';

import { IconCopy, IconCheck, IconUpload } from '@tabler/icons-react';

import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { motion } from 'framer-motion';

const MotionCard = motion(Card);

// Tipos aceitos pelo backend (Integration.type, ver conversas/models.py) -
// só whatsapp e instagram têm envio/recebimento de mensagem implementados
// de ponta a ponta hoje; os demais podem ser criados mas ainda não enviam
// nem recebem nada sozinhos.
const CHANNEL_TYPES = {
    whatsapp: { label: 'WhatsApp', icon: IconBrandWhatsapp, color: '#16a34a', gradient: 'linear-gradient(135deg,#16a34a,#22c55e)' },
    instagram: { label: 'Instagram', icon: IconBrandInstagram, color: '#db2777', gradient: 'linear-gradient(135deg,#db2777,#f472b6)' },
    messenger: { label: 'Messenger', icon: IconBrandMessenger, color: '#2563eb', gradient: 'linear-gradient(135deg,#2563eb,#60a5fa)' },
    email: { label: 'E-mail', icon: IconMail, color: '#ea580c', gradient: 'linear-gradient(135deg,#ea580c,#fb923c)' },
    google_calendar: {
        label: 'Google Agenda',
        icon: IconCalendarEvent,
        color: '#4285F4',
        gradient: 'linear-gradient(135deg,#4285F4,#34A853)'
    },
    web: { label: 'Widget de Chat', icon: IconWorldWww, color: '#7c3aed', gradient: 'linear-gradient(135deg,#7c3aed,#a855f7)' },
    voice: { label: 'Voz', icon: IconPhoneCall, color: '#0284c7', gradient: 'linear-gradient(135deg,#0284c7,#38bdf8)' },
    agent_workspace: {
        label: 'Workspace do agente',
        icon: IconRobot,
        color: '#475569',
        gradient: 'linear-gradient(135deg,#475569,#94a3b8)'
    },
    custom: { label: 'Personalizado', icon: IconSettings, color: '#64748b', gradient: 'linear-gradient(135deg,#64748b,#94a3b8)' }
};

const emptyForm = {
    displayName: '',
    type: 'whatsapp',
    status: 'active',
    // WhatsApp: W-API / Z-API (não-oficiais) ou official (Meta Cloud API)
    client_type: 'w_api',
    phoneNumber: '',
    // Só usado quando client_type === 'official' - é o phone_number_id que
    // a Meta gera pro número, usado tanto pra resolver o tenant no webhook
    // quanto pra montar a URL de envio (graph.facebook.com/{id}/messages).
    phoneNumberId: '',
    // Também só pra client_type === 'official' - o ID da conta WhatsApp
    // Business (WABA) na Meta. Sem isso, tudo que consulta a Graph API
    // (listar/criar templates) falha com "sem WABA ID configurado".
    businessManagerId: '',
    // Idem, também só pra client_type === 'official' - o App ID da Meta
    // for Developers. Sem isso, upload de imagem de header de template
    // (protocolo de upload resumível da Graph API) não funciona.
    accountAppId: '',
    // Instagram, WhatsApp e E-mail compartilham esses dois campos genéricos
    // no backend (client_instance_id / client_instance_secret) - o rótulo
    // exibido muda conforme o tipo/provedor do canal (pra e-mail: endereço
    // e senha da caixa).
    client_instance_id: '',
    client_instance_secret: '',
    // Só pra client_type === 'evolution_api' (self-hospedada, URL própria
    // por conta, diferente da w-api que é fixa/compartilhada) - a
    // instância é criada manualmente (painel/API da própria Evolution,
    // fora do CRM, mesmo fluxo de w_api/z_api) - `client_instance_id`/
    // `client_instance_secret` acima guardam o instanceName/token já
    // criados; só falta saber a URL do servidor pra consultar QR/status.
    evolution_base_url: '',
    // Só usados quando type === 'email' - servidor/porta de IMAP (receber)
    // e SMTP (responder) da caixa do cliente.
    email_imap_host: '',
    email_imap_port: 993,
    email_smtp_host: '',
    email_smtp_port: 465,
    // Só usados quando type === 'web' (Widget de Chat) - cor principal do
    // widget e domínios autorizados a embutir o script (vazio = qualquer).
    widget_color: '#0099ff',
    widget_allowed_domains: '',
    widget_welcome_message: 'Olá! Como posso ajudar?',
    // Quem responde esse canal
    defaultResponderEnabled_id: '',
    chatbotResponder_id: '',
    // Equipe pra qual toda conversa nova (ou reaberta) nesse canal cai
    // automaticamente - obrigatório pros tipos em MESSAGE_CHANNEL_TYPES.
    default_team_id: ''
};

// Mesma lista de `conversas/views.py::MESSAGE_CHANNEL_TYPES` - só esses
// tipos geram conversa de verdade (têm inbox), então só eles exigem
// equipe padrão. google_calendar (sincronização, não atendimento) fica
// de fora.
const MESSAGE_CHANNEL_TYPES = ['whatsapp', 'messenger', 'web', 'instagram', 'email'];

const MessageChannels = () => {
    const theme = useTheme();

    const currentUser = useSelector((state) => state.account.user);
    const isAdmin = currentUser?.role === 'admin';

    const [channels, setChannels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const history = useHistory();
    const location = useLocation();

    const [status, setStatus] = useState({ type: '', message: '' });

    const [openModal, setOpenModal] = useState(false);
    const [editingChannel, setEditingChannel] = useState(null);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState(emptyForm);

    // Widget de Chat: id do canal recém-criado (pra poder mostrar o bloco
    // de instalação/logo sem precisar fechar e reabrir o modal), estado do
    // upload de logo e do botão "copiar código".
    const [savedIntegrationId, setSavedIntegrationId] = useState(null);
    const [widgetLogoKey, setWidgetLogoKey] = useState(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [codeCopied, setCodeCopied] = useState(false);

    // WhatsApp Não Oficial (w_api): status de conexão + QR Code, com
    // atualização automática (QR expira em ~20s na w-api, então busca um
    // novo um pouco antes disso; status é checado com mais frequência pra
    // detectar rápido quando o cliente escaneia).
    const [waStatus, setWaStatus] = useState(null); // 'checking' | 'connected' | 'disconnected' | 'error' | null
    const [waQrCode, setWaQrCode] = useState(null);
    const waStatusIntervalRef = useRef(null);
    const waQrIntervalRef = useRef(null);
    // Recarregar a instância w-api - pra quando a conexão trava (aparece
    // como conectada mas para de entregar mensagem nova) sem a sessão do
    // WhatsApp ter caído de verdade, sem precisar escanear o QR Code de novo.
    const [restartingWApi, setRestartingWApi] = useState(false);

    // WhatsApp Não Oficial (w_api / z_api): URL de webhook por integração,
    // que precisa ser colada no painel do próprio provedor (W-API/Z-API)
    // pra ele saber pra onde mandar as mensagens recebidas. Sem isso
    // configurado do lado do provedor, nada chega aqui - o log fica mudo.
    const [webhookUrl, setWebhookUrl] = useState(null);
    const [loadingWebhookUrl, setLoadingWebhookUrl] = useState(false);
    const [webhookUrlCopied, setWebhookUrlCopied] = useState(false);

    // Google Agenda: gestão admin-only de TODAS as contas Google
    // conectadas na conta (o backend já garante que só admin vê tudo -
    // aqui só espelha isso na UI, é o admin quem decide pra qual usuário
    // conectar uma conta nova e quem mais enxerga cada uma).
    const [googleCalendarConnections, setGoogleCalendarConnections] = useState([]);
    const [loadingGoogleCalendarConnections, setLoadingGoogleCalendarConnections] = useState(false);
    const [connectingGoogleFor, setConnectingGoogleFor] = useState('');
    const [connectingGoogle, setConnectingGoogle] = useState(false);
    const [savingGoogleConnectionId, setSavingGoogleConnectionId] = useState(null);
    const [teamUsers, setTeamUsers] = useState([]);
    // Equipes cadastradas (`Teams`) - pro seletor de "Equipe padrão" do
    // canal. Nome diferente de `teamUsers` (que é outra coisa: usuários
    // da conta, usado no seletor do Google Agenda) de propósito, pra não
    // confundir os dois.
    const [equipes, setEquipes] = useState([]);
    const [rotatingWebhookUrl, setRotatingWebhookUrl] = useState(false);

    const [appId, setAppId] = useState(null);
    const [listaSwitchboardsIntegrations, setListaSwitchboardsIntegrations] = useState([]);
    const [listaChatbots, setListaChatbots] = useState([]);

    const pegar_app_padrao = async () => {
        try {
            const response = await api.get('v1/apps/default/');
            setAppId(response.data?.app?.id || null);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar o App.' });
        }
    };

    useEffect(() => {
        pegar_app_padrao();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadChannels();
        loadSwitchboardIntegrations();
        loadChatbots();
        loadEquipes();
        if (isAdmin) loadTeamUsers();
    }, [appId]);

    // Volta do redirect OAuth do Google (`?google_calendar=connected|error`) -
    // o backend manda pra cá porque quem inicia a conexão agora é sempre
    // um admin nessa tela, não mais "Ajustes da Conta".
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const resultado = params.get('google_calendar');
        if (!resultado) return;

        if (resultado === 'connected') {
            setStatus({
                type: 'success',
                message: 'Conta Google conectada! Abra o canal "Google Agenda" pra ver e atribuir a novos usuários.'
            });
        } else {
            setStatus({ type: 'error', message: 'Erro ao conectar a conta Google. Tente novamente.' });
        }
        history.replace('/dashboard/admin/channels');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const loadTeamUsers = async () => {
        try {
            const response = await api.get('users/');
            setTeamUsers(response.data?.users || response.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    const loadEquipes = async () => {
        if (!appId) return;
        try {
            const response = await api.get(`v1/apps/${appId}/teams/`);
            setEquipes(response.data?.teams || []);
        } catch (error) {
            console.error('Erro ao carregar equipes', error);
        }
    };

    const loadGoogleCalendarConnections = async () => {
        setLoadingGoogleCalendarConnections(true);
        try {
            const response = await api.get(`v1/apps/${appId}/google_calendar/connections/`);
            setGoogleCalendarConnections(response.data || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingGoogleCalendarConnections(false);
        }
    };

    useEffect(() => {
        if (openModal && form.type === 'google_calendar' && isAdmin && appId) {
            loadGoogleCalendarConnections();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openModal, form.type, isAdmin, appId]);

    const handleConnectGoogleAccount = async () => {
        setConnectingGoogle(true);
        try {
            const response = await api.get(`v1/apps/${appId}/google_calendar/authorize_url/`, {
                // O backend só conhece UM domínio padrão fixo pra voltar
                // depois do Google - manda o subdomínio de quem está
                // clicando agora (ex: acin.loged.com.br) pra ele saber pra
                // onde redirecionar no final, em vez de sempre cair no
                // domínio principal (crm.loged.com.br).
                params: { for_user_id: connectingGoogleFor || currentUser?.id, redirect_origin: window.location.origin }
            });
            window.location.href = response.data.url;
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao iniciar conexão com o Google.' });
            setConnectingGoogle(false);
        }
    };

    const handleToggleGoogleConnectionSync = async (connectionId, checked) => {
        setSavingGoogleConnectionId(connectionId);
        try {
            await api.patch(`v1/apps/${appId}/google_calendar/connections/${connectionId}/`, { sync_enabled: checked });
            setGoogleCalendarConnections((prev) => prev.map((c) => (c.id === connectionId ? { ...c, sync_enabled: checked } : c)));
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao atualizar a sincronização.' });
        } finally {
            setSavingGoogleConnectionId(null);
        }
    };

    const handleToggleSyncedCalendar = async (connectionId, syncedCalendarId, checked) => {
        setSavingGoogleConnectionId(connectionId);
        try {
            await api.patch(`v1/apps/${appId}/google_calendar/connections/${connectionId}/calendars/${syncedCalendarId}/`, {
                sync_enabled: checked
            });
            setGoogleCalendarConnections((prev) =>
                prev.map((c) =>
                    c.id === connectionId
                        ? {
                              ...c,
                              synced_calendars: c.synced_calendars.map((sc) =>
                                  sc.id === syncedCalendarId ? { ...sc, sync_enabled: checked } : sc
                              )
                          }
                        : c
                )
            );
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao atualizar o calendário.' });
        } finally {
            setSavingGoogleConnectionId(null);
        }
    };

    const handleChangeGoogleAssignedUsers = async (connectionId, userIds) => {
        setSavingGoogleConnectionId(connectionId);
        try {
            const response = await api.patch(`v1/apps/${appId}/google_calendar/connections/${connectionId}/`, {
                assigned_user_ids: userIds
            });
            setGoogleCalendarConnections((prev) =>
                prev.map((c) => (c.id === connectionId ? { ...c, assigned_users: response.data.assigned_users } : c))
            );
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao atualizar quem vê essa conta.' });
        } finally {
            setSavingGoogleConnectionId(null);
        }
    };

    const handleDisconnectGoogleConnection = async (connectionId) => {
        const confirmDisconnect = window.confirm(
            'Desconectar essa conta Google? Quem estiver com ela atribuída deixa de ver os eventos dela.'
        );
        if (!confirmDisconnect) return;

        setSavingGoogleConnectionId(connectionId);
        try {
            await api.delete(`v1/apps/${appId}/google_calendar/connections/${connectionId}/`);
            setGoogleCalendarConnections((prev) => prev.filter((c) => c.id !== connectionId));
            setStatus({ type: 'success', message: 'Conta Google desconectada.' });
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao desconectar essa conta Google.' });
        } finally {
            setSavingGoogleConnectionId(null);
        }
    };

    const loadChannels = async () => {
        setLoading(true);

        try {
            const response = await api.get(`v1/apps/${appId}/integrations/`);
            setChannels(response.data?.integrations || []);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar canais.' });
        } finally {
            setLoading(false);
        }
    };

    const loadSwitchboardIntegrations = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/switchboardIntegrations/`);
            setListaSwitchboardsIntegrations(response.data?.switchboardIntegrations || []);
        } catch (error) {
            console.error('Erro ao carregar Switchboard Integrations', error);
        }
    };

    const loadChatbots = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/chatbots/`);
            setListaChatbots(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Erro ao carregar chatbots', error);
        }
    };

    const filteredChannels = useMemo(() => {
        if (!search.trim()) {
            return channels;
        }

        const term = search.toLowerCase();

        return channels.filter(
            (channel) =>
                channel.displayName?.toLowerCase().includes(term) ||
                channel.type?.toLowerCase().includes(term) ||
                channel.phoneNumber?.toLowerCase().includes(term) ||
                channel.client_instance_id?.toLowerCase().includes(term)
        );
    }, [search, channels]);

    const handleOpenCreate = () => {
        setEditingChannel(null);
        setForm(emptyForm);
        setSavedIntegrationId(null);
        setWidgetLogoKey(null);
        setCodeCopied(false);
        setOpenModal(true);
    };

    const handleOpenEdit = (channel) => {
        setEditingChannel(channel);

        setForm({
            displayName: channel.displayName || '',
            type: channel.type || 'whatsapp',
            status: channel.status === 'inactive' ? 'inactive' : 'active',
            client_type: channel.client_type || 'w_api',
            phoneNumber: channel.phoneNumber || '',
            phoneNumberId: channel.phoneNumberId || '',
            businessManagerId: channel.businessManagerId || '',
            accountAppId: channel.accountAppId || '',
            client_instance_id: channel.client_instance_id || '',
            client_instance_secret: channel.client_instance_secret || '',
            evolution_base_url: channel.evolution_base_url || '',
            email_imap_host: channel.email_imap_host || '',
            email_imap_port: channel.email_imap_port || 993,
            email_smtp_host: channel.email_smtp_host || '',
            email_smtp_port: channel.email_smtp_port || 465,
            widget_color: channel.brandColor ? `#${channel.brandColor.replace('#', '')}` : '#0099ff',
            widget_allowed_domains: (channel.widget_allowed_domains || []).join(', '),
            widget_welcome_message: channel.widget_welcome_message || 'Olá! Como posso ajudar?',
            defaultResponderEnabled_id: channel.defaultResponderEnabled_id?.id ?? '',
            chatbotResponder_id: channel.chatbotResponder_id?.id ?? '',
            default_team_id: channel.default_team_id || ''
        });

        setSavedIntegrationId(channel.id);
        setWidgetLogoKey(channel.widget_logo_key || null);
        setCodeCopied(false);
        setOpenModal(true);
    };

    // WhatsApp Não Oficial (w_api) - QR Code + status de conexão
    const stopWApiPolling = () => {
        if (waStatusIntervalRef.current) {
            clearInterval(waStatusIntervalRef.current);
            waStatusIntervalRef.current = null;
        }
        if (waQrIntervalRef.current) {
            clearInterval(waQrIntervalRef.current);
            waQrIntervalRef.current = null;
        }
    };

    const fetchWApiQrCode = async (integrationId) => {
        try {
            const response = await api.get(`v1/apps/${appId}/integrations/${integrationId}/w_api_qr_code/`);
            setWaQrCode(response.data?.qrcode || null);
        } catch (error) {
            console.error(error);
        }
    };

    const checkWApiStatus = async (integrationId) => {
        setWaStatus((prev) => prev || 'checking');
        try {
            const response = await api.get(`v1/apps/${appId}/integrations/${integrationId}/w_api_status/`);
            if (response.data?.connected) {
                stopWApiPolling();
                setWaStatus('connected');
                setWaQrCode(null);
                return;
            }

            setWaStatus('disconnected');

            // Já tem um QR na tela e o polling rodando - só a checagem de
            // status (mais frequente) precisava rodar agora.
            if (waQrIntervalRef.current) return;

            await fetchWApiQrCode(integrationId);
            waStatusIntervalRef.current = setInterval(() => checkWApiStatus(integrationId), 5000);
            waQrIntervalRef.current = setInterval(() => fetchWApiQrCode(integrationId), 18000);
        } catch (error) {
            console.error(error);
            setWaStatus('error');
        }
    };

    const handleRestartWApiInstance = async (integrationId) => {
        setRestartingWApi(true);
        try {
            const response = await api.post(`v1/apps/${appId}/integrations/${integrationId}/w_api_restart/`);
            setStatus({ type: 'success', message: response.data?.message || 'Conexão recarregada com sucesso.' });
            checkWApiStatus(integrationId);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao recarregar a conexão.' });
        } finally {
            setRestartingWApi(false);
        }
    };

    // WhatsApp Não Oficial (evolution_api, self-hospedada) - mesmo padrão
    // de QR Code/status/restart do w_api acima, reaproveitando os MESMOS
    // estados (`waStatus`/`waQrCode`/`restartingWApi`/os refs de
    // polling) - só um provedor por vez é relevante num mesmo modal
    // (o valor de `form.client_type` decide qual conjunto de funções
    // chamar), então não precisa de estado duplicado.
    const fetchEvolutionQrCode = async (integrationId) => {
        try {
            const response = await api.get(`v1/apps/${appId}/integrations/${integrationId}/evolution_qr_code/`);
            setWaQrCode(response.data?.qrcode || null);
        } catch (error) {
            console.error(error);
        }
    };

    const checkEvolutionStatus = async (integrationId) => {
        setWaStatus((prev) => prev || 'checking');
        try {
            const response = await api.get(`v1/apps/${appId}/integrations/${integrationId}/evolution_status/`);
            if (response.data?.connected) {
                stopWApiPolling();
                setWaStatus('connected');
                setWaQrCode(null);
                return;
            }

            setWaStatus('disconnected');

            if (waQrIntervalRef.current) return;

            await fetchEvolutionQrCode(integrationId);
            waStatusIntervalRef.current = setInterval(() => checkEvolutionStatus(integrationId), 5000);
            waQrIntervalRef.current = setInterval(() => fetchEvolutionQrCode(integrationId), 18000);
        } catch (error) {
            console.error(error);
            setWaStatus('error');
        }
    };

    const handleRestartEvolutionInstance = async (integrationId) => {
        setRestartingWApi(true);
        try {
            const response = await api.post(`v1/apps/${appId}/integrations/${integrationId}/evolution_restart/`);
            setStatus({ type: 'success', message: response.data?.message || 'Conexão recarregada com sucesso.' });
            checkEvolutionStatus(integrationId);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao recarregar a conexão.' });
        } finally {
            setRestartingWApi(false);
        }
    };

    const wApiIntegrationId = savedIntegrationId;

    useEffect(() => {
        if (
            openModal &&
            form.type === 'whatsapp' &&
            form.client_type === 'w_api' &&
            form.client_instance_id &&
            form.client_instance_secret &&
            wApiIntegrationId
        ) {
            checkWApiStatus(wApiIntegrationId);
        } else if (
            openModal &&
            form.type === 'whatsapp' &&
            form.client_type === 'evolution_api' &&
            form.client_instance_id &&
            form.client_instance_secret &&
            wApiIntegrationId
        ) {
            checkEvolutionStatus(wApiIntegrationId);
        } else {
            stopWApiPolling();
            setWaStatus(null);
            setWaQrCode(null);
        }

        return () => stopWApiPolling();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openModal, form.type, form.client_type, form.client_instance_id, form.client_instance_secret, wApiIntegrationId]);

    // WhatsApp Não Oficial (w_api / z_api): busca a URL de webhook dessa
    // integração assim que ela já tem um id salvo, pra exibir e o usuário
    // colar no painel do provedor.
    const WEBHOOK_URL_PATH_BY_KIND = { w_api: 'wapi-webhook', z_api: 'zapi-webhook', evolution_api: 'evolution-webhook' };

    const fetchWebhookUrl = async (integrationId, kind) => {
        setLoadingWebhookUrl(true);
        try {
            const path = WEBHOOK_URL_PATH_BY_KIND[kind] || 'wapi-webhook';
            const response = await api.get(`v1/apps/${appId}/integrations/${integrationId}/${path}/`);
            setWebhookUrl(response.data?.url || null);
        } catch (error) {
            console.error(error);
            setWebhookUrl(null);
        } finally {
            setLoadingWebhookUrl(false);
        }
    };

    const handleRotateWebhookUrl = async (integrationId, kind) => {
        const confirmRotate = window.confirm(
            'Isso vai gerar uma URL nova e invalidar a atual. Você vai precisar atualizar o painel do provedor com a nova URL. Continuar?'
        );
        if (!confirmRotate) return;

        setRotatingWebhookUrl(true);
        try {
            const path = WEBHOOK_URL_PATH_BY_KIND[kind] || 'wapi-webhook';
            const response = await api.post(`v1/apps/${appId}/integrations/${integrationId}/${path}/`, { rotate: true });
            setWebhookUrl(response.data?.url || null);
            setWebhookUrlCopied(false);
            setStatus({ type: 'success', message: 'Nova URL de webhook gerada.' });
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao gerar nova URL de webhook.' });
        } finally {
            setRotatingWebhookUrl(false);
        }
    };

    const handleCopyWebhookUrl = async () => {
        if (!webhookUrl) return;
        try {
            await navigator.clipboard.writeText(webhookUrl);
            setWebhookUrlCopied(true);
            setTimeout(() => setWebhookUrlCopied(false), 2000);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao copiar a URL.' });
        }
    };

    useEffect(() => {
        if (
            openModal &&
            appId &&
            form.type === 'whatsapp' &&
            (form.client_type === 'w_api' || form.client_type === 'z_api' || form.client_type === 'evolution_api') &&
            wApiIntegrationId
        ) {
            fetchWebhookUrl(wApiIntegrationId, form.client_type);
        } else {
            setWebhookUrl(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openModal, appId, form.type, form.client_type, wApiIntegrationId]);

    const buildPayload = () => {
        const payload = {
            displayName: form.displayName,
            type: form.type,
            status: form.status,
            defaultResponderEnabled_id: form.defaultResponderEnabled_id,
            chatbotResponder_id: form.chatbotResponder_id,
            default_team_id: form.default_team_id || ''
        };

        if (form.type === 'whatsapp') {
            payload.client_type = form.client_type;
            payload.phoneNumber = form.phoneNumber;

            if (form.client_type === 'official') {
                payload.phoneNumberId = form.phoneNumberId;
                payload.businessManagerId = form.businessManagerId;
                payload.accountAppId = form.accountAppId;
                payload.client_instance_secret = form.client_instance_secret;
            } else {
                payload.client_instance_id = form.client_instance_id;
                payload.client_instance_secret = form.client_instance_secret;
            }

            if (form.client_type === 'evolution_api') {
                payload.evolution_base_url = form.evolution_base_url;
            }
        }

        if (form.type === 'instagram') {
            payload.client_instance_id = form.client_instance_id;
            payload.client_instance_secret = form.client_instance_secret;
        }

        if (form.type === 'email') {
            payload.client_instance_id = form.client_instance_id;
            payload.client_instance_secret = form.client_instance_secret;
            payload.email_imap_host = form.email_imap_host;
            payload.email_imap_port = form.email_imap_port;
            payload.email_smtp_host = form.email_smtp_host;
            payload.email_smtp_port = form.email_smtp_port;
        }

        if (form.type === 'web') {
            payload.widget_allowed_domains = form.widget_allowed_domains
                .split(',')
                .map((domain) => domain.trim())
                .filter(Boolean);
            payload.widget_welcome_message = form.widget_welcome_message;
        }

        if (!editingChannel && !savedIntegrationId) {
            // Só faz sentido na primeira criação - depois disso (edição, ou
            // 2º "Salvar" de um widget recém-criado) não queremos
            // sobrescrever a aparência que o canal já tem.
            payload.displayStyle = 'button';
            payload.actionColor = '0099ff';
            payload.conversationColor = '242424';
            payload.brandColor = '65758e';
            payload.canUserCreateMoreConversations = true;
        }

        if (form.type === 'web') {
            // A cor do widget é sempre a escolhida no formulário, tanto na
            // criação quanto na edição (sobrescreve o default acima).
            payload.brandColor = form.widget_color.replace('#', '');
        }

        return payload;
    };

    const handleSave = async () => {
        if (!form.displayName.trim()) {
            setStatus({ type: 'error', message: 'Informe um nome pro canal.' });
            return;
        }

        if (MESSAGE_CHANNEL_TYPES.includes(form.type) && !form.default_team_id) {
            setStatus({ type: 'error', message: 'Escolha a equipe padrão desse canal - toda conversa nova cai nela automaticamente.' });
            return;
        }

        setSaving(true);

        const integrationId = editingChannel?.id || savedIntegrationId;

        try {
            const payload = buildPayload();

            if (integrationId) {
                await api.patch(`v1/apps/${appId}/integrations/${integrationId}/`, payload);
                setStatus({ type: 'success', message: 'Canal atualizado com sucesso.' });
            } else {
                const response = await api.post(`v1/apps/${appId}/integrations/`, payload);
                setStatus({ type: 'success', message: 'Canal criado com sucesso.' });

                const isWhatsappNaoOficialRecemCriado =
                    form.type === 'whatsapp' &&
                    (form.client_type === 'w_api' || form.client_type === 'z_api' || form.client_type === 'evolution_api') &&
                    form.client_instance_id &&
                    form.client_instance_secret;

                if (form.type === 'web' || isWhatsappNaoOficialRecemCriado) {
                    // Mantém o modal aberto - pro widget, pra mostrar o bloco
                    // de instalação (upload de logo + snippet); pro WhatsApp
                    // Não Oficial (w_api/z_api/evolution_api), pra mostrar a
                    // URL de webhook (e o QR Code) - todos só existem depois
                    // que o canal já tem um id. Nos três casos a instância já
                    // foi criada manualmente fora do CRM (painel/API do
                    // próprio provedor) ANTES de preencher esse formulário -
                    // não tem "provisionamento automático" nenhum aqui.
                    setSavedIntegrationId(response.data?.integrationId || null);
                    setSaving(false);
                    loadChannels();
                    return;
                }
            }

            setOpenModal(false);
            loadChannels();
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao salvar canal.' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (channel) => {
        const confirmDelete = window.confirm('Deseja realmente excluir este canal? Essa ação não pode ser desfeita.');

        if (!confirmDelete) {
            return;
        }

        try {
            await api.delete(`v1/apps/${appId}/integrations/${channel.id}/`);
            setChannels((prev) => prev.filter((c) => c.id !== channel.id));
            setStatus({ type: 'success', message: 'Canal removido com sucesso.' });
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao excluir canal.' });
        }
    };

    const handleUploadLogo = async (file) => {
        if (!file || !savedIntegrationId) return;

        setUploadingLogo(true);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await api.post(`v1/apps/${appId}/integrations/${savedIntegrationId}/widget-logo/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setWidgetLogoKey(response.data?.widget_logo_key || null);
            setStatus({ type: 'success', message: 'Logo enviado com sucesso.' });
            loadChannels();
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao enviar o logo.' });
        } finally {
            setUploadingLogo(false);
        }
    };

    const buildWidgetSnippet = () => {
        const origin = window.location.origin;
        return `<script
  src="${origin}/widget.js"
  data-app-id="${appId}"
  data-integration-id="${savedIntegrationId}"
  data-api-server="${config.API_SERVER}"
  data-brand-color="${form.widget_color.replace('#', '')}"${
      widgetLogoKey
          ? `
  data-logo-key="${widgetLogoKey}"`
          : ''
  }
  async
></script>`;
    };

    const handleCopyCode = async () => {
        try {
            await navigator.clipboard.writeText(buildWidgetSnippet());
            setCodeCopied(true);
            setTimeout(() => setCodeCopied(false), 2000);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao copiar o código.' });
        }
    };

    const responderLabel = (channel) => {
        if (channel.defaultResponderEnabled_id?.name) {
            return `Responde: ${channel.defaultResponderEnabled_id.name}`;
        }
        return 'Sem responsável definido (fila humana)';
    };

    return (
        <Box>
            {/* HERO */}
            <Button
                variant="outlined"
                startIcon={<IconArrowLeft size={18} />}
                onClick={() => history.goBack()}
                sx={{ borderRadius: '12px', textTransform: 'none' }}
            >
                Voltar
            </Button>

            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '28px',
                    p: { xs: 3, md: 5 },
                    mb: 4,
                    mt: 2,
                    background:
                        theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg,#111827 0%,#1e293b 100%)'
                            : 'linear-gradient(135deg,#f0fdf4 0%,#ffffff 100%)',
                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.08)',
                    boxShadow: theme.palette.mode === 'dark' ? '0 10px 40px rgba(0,0,0,0.35)' : '0 10px 40px rgba(15,23,42,0.08)'
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        width: 320,
                        height: 320,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(34,197,94,0.18), rgba(16,185,129,0.15))',
                        top: -120,
                        right: -80,
                        filter: 'blur(40px)'
                    }}
                />

                <Box sx={{ position: 'relative', zIndex: 2 }}>
                    <Chip icon={<IconPlugConnected size={14} />} label="Comunicação" sx={{ mb: 2, fontWeight: 700 }} />

                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: 900,
                            fontSize: { xs: '2rem', md: '3rem' },
                            letterSpacing: '-2px',
                            mb: 1,
                            background: 'linear-gradient(90deg,#16a34a,#10b981)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}
                    >
                        Canais de Mensagem
                    </Typography>

                    <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 700, lineHeight: 1.8, mb: 4 }}>
                        Gerencie suas conexões de WhatsApp e Instagram (e outros canais) direto por aqui — criação, credenciais e quem
                        responde cada canal.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <OutlinedInput
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar canal..."
                            startAdornment={
                                <InputAdornment position="start">
                                    <IconSearch size={18} />
                                </InputAdornment>
                            }
                            sx={{
                                flex: 1,
                                minWidth: 280,
                                height: 56,
                                borderRadius: '18px',
                                background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fff'
                            }}
                        />

                        <Button
                            variant="contained"
                            startIcon={<IconPlus size={18} />}
                            onClick={handleOpenCreate}
                            disabled={!appId}
                            sx={{
                                height: 56,
                                px: 3,
                                borderRadius: '18px',
                                textTransform: 'none',
                                fontWeight: 700,
                                background: 'linear-gradient(135deg,#16a34a,#10b981)',
                                boxShadow: '0 10px 25px rgba(34,197,94,0.25)'
                            }}
                        >
                            Novo Canal
                        </Button>
                    </Box>
                </Box>
            </Box>

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 3, borderRadius: '16px' }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            {/* GRID */}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress />
                </Box>
            ) : filteredChannels.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: '16px' }}>
                    Nenhum canal criado ainda. Clique em "Novo Canal" pra conectar seu primeiro WhatsApp ou Instagram.
                </Alert>
            ) : (
                <Grid container spacing={3}>
                    {filteredChannels.map((channel, index) => {
                        const config = CHANNEL_TYPES[channel.type] || CHANNEL_TYPES.custom;
                        const Icon = config.icon;
                        const Color = config.color;

                        return (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={channel.id}>
                                <MotionCard
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.03 }}
                                    whileHover={{ y: -5 }}
                                    sx={{
                                        borderRadius: '24px',
                                        overflow: 'hidden',
                                        height: '100%',
                                        border:
                                            theme.palette.mode === 'dark'
                                                ? '1px solid rgba(255,255,255,0.06)'
                                                : '1px solid rgba(15,23,42,0.06)',
                                        background:
                                            theme.palette.mode === 'dark' ? 'linear-gradient(180deg,#111827 0%,#0f172a 100%)' : '#fff',
                                        boxShadow:
                                            theme.palette.mode === 'dark'
                                                ? '0 10px 30px rgba(0,0,0,0.3)'
                                                : '0 10px 30px rgba(15,23,42,0.06)'
                                    }}
                                >
                                    <CardActionArea onClick={() => handleOpenEdit(channel)} sx={{ height: '100%' }}>
                                        <CardContent sx={{ p: 3 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Avatar
                                                    sx={{
                                                        width: 64,
                                                        height: 64,
                                                        background: config.gradient,
                                                        boxShadow: `0 10px 25px ${config.color}33`
                                                    }}
                                                >
                                                    <Icon size={30} color={Color} />
                                                </Avatar>

                                                <Chip
                                                    label={channel.status === 'active' ? 'Ativo' : 'Inativo'}
                                                    size="small"
                                                    color={channel.status === 'active' ? 'success' : 'default'}
                                                    sx={{ fontWeight: 700 }}
                                                />
                                            </Box>

                                            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                                                {channel.displayName}
                                            </Typography>

                                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                                {config.label}
                                            </Typography>

                                            <Divider sx={{ my: 2 }} />

                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                    >
                                                        {channel.type === 'whatsapp' && (channel.phoneNumber || 'Número não configurado')}
                                                        {(channel.type === 'instagram' || channel.type === 'email') &&
                                                            (channel.client_instance_id || 'Conta não configurada')}
                                                        {channel.type !== 'whatsapp' &&
                                                            channel.type !== 'instagram' &&
                                                            channel.type !== 'email' &&
                                                            ' '}
                                                    </Typography>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <IconMessages size={16} />
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                    >
                                                        {responderLabel(channel)}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    Editar canal
                                                </Typography>

                                                <IconChevronRight size={18} />
                                            </Box>
                                        </CardContent>
                                    </CardActionArea>
                                </MotionCard>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* MODAL */}

            <Dialog
                open={openModal}
                onClose={() => setOpenModal(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{ sx: { borderRadius: '24px' } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 800 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconMessageCircle size={22} />
                        {editingChannel ? 'Editar Canal' : 'Novo Canal'}
                    </Box>

                    <IconButton onClick={() => setOpenModal(false)}>
                        <IconX size={18} />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Nome do canal"
                                value={form.displayName}
                                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Tipo do canal"
                                value={form.type}
                                onChange={(e) => setForm({ ...form, type: e.target.value })}
                            >
                                {Object.entries(CHANNEL_TYPES).map(([key, value]) => (
                                    <MenuItem key={key} value={key}>
                                        {value.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {MESSAGE_CHANNEL_TYPES.includes(form.type) && (
                            <Grid item xs={12} md={6}>
                                <TextField
                                    select
                                    fullWidth
                                    required
                                    label="Equipe padrão"
                                    value={form.default_team_id}
                                    onChange={(e) => setForm({ ...form, default_team_id: e.target.value })}
                                    helperText="Toda conversa nova (ou reaberta) nesse canal cai automaticamente nessa equipe."
                                >
                                    {equipes.length === 0 ? (
                                        <MenuItem disabled>Nenhuma equipe cadastrada - crie uma em Configurações &gt; Equipes</MenuItem>
                                    ) : (
                                        equipes.map((eq) => (
                                            <MenuItem key={eq.id} value={eq.id}>
                                                {eq.name}
                                            </MenuItem>
                                        ))
                                    )}
                                </TextField>
                            </Grid>
                        )}

                        {form.type === 'whatsapp' && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Provedor"
                                        value={form.client_type}
                                        onChange={(e) => setForm({ ...form, client_type: e.target.value })}
                                    >
                                        <MenuItem value="w_api">API Não Oficial 1</MenuItem>
                                        <MenuItem value="z_api">API Não Oficial 2</MenuItem>
                                        <MenuItem value="evolution_api">Evolution API (self-hospedada)</MenuItem>
                                        <MenuItem value="official">API Oficial (Meta Cloud API)</MenuItem>
                                    </TextField>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Número de telefone"
                                        placeholder="5511999999999"
                                        value={form.phoneNumber}
                                        onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
                                    />
                                </Grid>

                                {form.client_type === 'official' ? (
                                    <>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Phone Number ID"
                                                helperText="Painel da Meta > WhatsApp > Introdução (ID do número de telefone)"
                                                value={form.phoneNumberId}
                                                onChange={(e) => setForm({ ...form, phoneNumberId: e.target.value })}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Token de acesso (System User)"
                                                value={form.client_instance_secret}
                                                onChange={(e) => setForm({ ...form, client_instance_secret: e.target.value })}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="WABA ID (WhatsApp Business Account)"
                                                helperText="Painel da Meta > WhatsApp > Configuração da API (ID da conta do WhatsApp Business) - necessário pra listar/criar templates"
                                                value={form.businessManagerId}
                                                onChange={(e) => setForm({ ...form, businessManagerId: e.target.value })}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="App ID (Meta for Developers)"
                                                helperText="developers.facebook.com > seu app > Configurações básicas - necessário pra upload de imagem de header de template"
                                                value={form.accountAppId}
                                                onChange={(e) => setForm({ ...form, accountAppId: e.target.value })}
                                            />
                                        </Grid>
                                    </>
                                ) : (
                                    <>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label={form.client_type === 'evolution_api' ? 'Nome da instância' : 'Instance ID'}
                                                helperText={
                                                    form.client_type === 'evolution_api'
                                                        ? 'Mesmo nome usado ao criar a instância no painel/API da sua Evolution API.'
                                                        : undefined
                                                }
                                                value={form.client_instance_id}
                                                onChange={(e) => setForm({ ...form, client_instance_id: e.target.value })}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Token"
                                                value={form.client_instance_secret}
                                                onChange={(e) => setForm({ ...form, client_instance_secret: e.target.value })}
                                            />
                                        </Grid>

                                        {form.client_type === 'evolution_api' && (
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="URL base da Evolution API"
                                                    placeholder="https://evolution.seudominio.com.br"
                                                    helperText="O endereço do servidor onde essa instância foi criada."
                                                    value={form.evolution_base_url}
                                                    onChange={(e) => setForm({ ...form, evolution_base_url: e.target.value })}
                                                />
                                            </Grid>
                                        )}

                                        {wApiIntegrationId ? (
                                            <Grid item xs={12}>
                                                <Box
                                                    sx={{
                                                        p: 2.5,
                                                        borderRadius: '16px',
                                                        border: '1px solid #e2e8f0',
                                                        background: '#f8fafc'
                                                    }}
                                                >
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                                        URL do Webhook
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                                                        {form.client_type === 'evolution_api' ? (
                                                            <>
                                                                Configure esta URL como webhook dessa instância na sua Evolution API
                                                                (painel ou <code>POST /webhook/set/&#123;instance&#125;</code>), habilitando os
                                                                eventos <code>MESSAGES_UPSERT</code>, <code>CONNECTION_UPDATE</code>,{' '}
                                                                <code>MESSAGES_UPDATE</code> e a opção de mídia em base64. Sem isso, as
                                                                mensagens recebidas nunca chegam até aqui.
                                                            </>
                                                        ) : (
                                                            <>
                                                                Cole esta URL no painel da {form.client_type === 'w_api' ? 'API NÃO OFICIAL' : 'Z-API'}{' '}
                                                                (campo de webhook / "ao receber"). Sem isso configurado do lado do provedor, as
                                                                mensagens recebidas nunca chegam até aqui.
                                                            </>
                                                        )}
                                                    </Typography>

                                                    {loadingWebhookUrl ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                            <CircularProgress size={18} />
                                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                                Carregando URL...
                                                            </Typography>
                                                        </Box>
                                                    ) : webhookUrl ? (
                                                        <>
                                                            <OutlinedInput
                                                                fullWidth
                                                                readOnly
                                                                value={webhookUrl}
                                                                size="small"
                                                                sx={{ mb: 1, background: '#fff', fontSize: '0.85rem' }}
                                                                endAdornment={
                                                                    <InputAdornment position="end">
                                                                        <IconButton onClick={handleCopyWebhookUrl} edge="end" size="small">
                                                                            {webhookUrlCopied ? (
                                                                                <IconCheck size={18} />
                                                                            ) : (
                                                                                <IconCopy size={18} />
                                                                            )}
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                }
                                                            />
                                                            <Button
                                                                size="small"
                                                                disabled={rotatingWebhookUrl}
                                                                onClick={() => handleRotateWebhookUrl(wApiIntegrationId, form.client_type)}
                                                                sx={{ textTransform: 'none' }}
                                                            >
                                                                {rotatingWebhookUrl ? 'Gerando...' : 'Gerar nova URL'}
                                                            </Button>
                                                        </>
                                                    ) : (
                                                        <Alert severity="error">Não foi possível carregar a URL do webhook.</Alert>
                                                    )}
                                                </Box>
                                            </Grid>
                                        ) : (
                                            <Grid item xs={12}>
                                                <Alert severity="info" sx={{ borderRadius: '12px' }}>
                                                    Salve o canal para gerar a URL do webhook que você precisa configurar no painel do
                                                    provedor.
                                                </Alert>
                                            </Grid>
                                        )}

                                        {(form.client_type === 'w_api' || form.client_type === 'evolution_api') && wApiIntegrationId && (
                                            <Grid item xs={12}>
                                                <Box
                                                    sx={{
                                                        p: 3,
                                                        borderRadius: '16px',
                                                        border: '1px solid #e2e8f0',
                                                        background: '#f8fafc',
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    {waStatus === 'checking' && (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: 1.5
                                                            }}
                                                        >
                                                            <CircularProgress size={20} />
                                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                                Verificando conexão...
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {waStatus === 'connected' && (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: 1.5,
                                                                alignItems: 'center'
                                                            }}
                                                        >
                                                            <Alert severity="success" sx={{ width: '100%' }}>
                                                                WhatsApp conectado!
                                                            </Alert>
                                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                                <Button
                                                                    size="small"
                                                                    onClick={() =>
                                                                        form.client_type === 'evolution_api'
                                                                            ? checkEvolutionStatus(wApiIntegrationId)
                                                                            : checkWApiStatus(wApiIntegrationId)
                                                                    }
                                                                >
                                                                    Verificar novamente
                                                                </Button>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    disabled={restartingWApi}
                                                                    startIcon={restartingWApi ? <CircularProgress size={14} /> : <IconRefresh size={16} />}
                                                                    onClick={() =>
                                                                        form.client_type === 'evolution_api'
                                                                            ? handleRestartEvolutionInstance(wApiIntegrationId)
                                                                            : handleRestartWApiInstance(wApiIntegrationId)
                                                                    }
                                                                    sx={{ textTransform: 'none' }}
                                                                >
                                                                    {restartingWApi ? 'Recarregando...' : 'Recarregar conexão'}
                                                                </Button>
                                                            </Box>
                                                            <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                                                                Se parou de receber mensagens novas mesmo aparecendo conectado, use
                                                                "Recarregar conexão" antes de desconectar e escanear o QR Code de novo.
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {waStatus === 'disconnected' && (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: 1.5,
                                                                alignItems: 'center'
                                                            }}
                                                        >
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                Escaneie o QR Code com o WhatsApp que deseja conectar.
                                                            </Typography>

                                                            {waQrCode ? (
                                                                <Box
                                                                    component="img"
                                                                    src={waQrCode}
                                                                    alt="QR Code de conexão do WhatsApp"
                                                                    sx={{
                                                                        width: 220,
                                                                        height: 220,
                                                                        borderRadius: '12px',
                                                                        border: '1px solid #e2e8f0'
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Box
                                                                    sx={{
                                                                        width: 220,
                                                                        height: 220,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center'
                                                                    }}
                                                                >
                                                                    <CircularProgress size={28} />
                                                                </Box>
                                                            )}

                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <CircularProgress size={14} />
                                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                                    Atualizando automaticamente...
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    )}

                                                    {waStatus === 'error' && (
                                                        <Alert severity="error">
                                                            Não foi possível consultar a conexão. Tente novamente.
                                                        </Alert>
                                                    )}
                                                </Box>
                                            </Grid>
                                        )}
                                    </>
                                )}
                            </>
                        )}

                        {form.type === 'instagram' && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Instagram Business Account ID"
                                        helperText="GET graph.instagram.com/v25.0/me?fields=id&access_token=..."
                                        value={form.client_instance_id}
                                        onChange={(e) => setForm({ ...form, client_instance_id: e.target.value })}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Access Token"
                                        value={form.client_instance_secret}
                                        onChange={(e) => setForm({ ...form, client_instance_secret: e.target.value })}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Alert severity="info" sx={{ borderRadius: '12px' }}>
                                        O Instagram não permite iniciar conversas — só responder depois que o cliente manda a primeira
                                        mensagem. O webhook (recebimento) já está configurado globalmente, não precisa de nada extra aqui.
                                    </Alert>
                                </Grid>
                            </>
                        )}

                        {form.type === 'email' && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Endereço de e-mail"
                                        placeholder="contato@seudominio.com.br"
                                        value={form.client_instance_id}
                                        onChange={(e) => setForm({ ...form, client_instance_id: e.target.value })}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        type="password"
                                        label="Senha da caixa de e-mail"
                                        value={form.client_instance_secret}
                                        onChange={(e) => setForm({ ...form, client_instance_secret: e.target.value })}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Servidor IMAP (receber)"
                                        placeholder="imap.hostinger.com"
                                        value={form.email_imap_host}
                                        onChange={(e) => setForm({ ...form, email_imap_host: e.target.value })}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Porta IMAP"
                                        value={form.email_imap_port}
                                        onChange={(e) => setForm({ ...form, email_imap_port: Number(e.target.value) || '' })}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Servidor SMTP (enviar)"
                                        placeholder="smtp.hostinger.com"
                                        value={form.email_smtp_host}
                                        onChange={(e) => setForm({ ...form, email_smtp_host: e.target.value })}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Porta SMTP"
                                        value={form.email_smtp_port}
                                        onChange={(e) => setForm({ ...form, email_smtp_port: Number(e.target.value) || '' })}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Alert severity="info" sx={{ borderRadius: '12px' }}>
                                        A caixa é verificada a cada 5 minutos (não é em tempo real como WhatsApp/Instagram). As portas mais
                                        comuns são 993 (IMAP) e 465 (SMTP), ambas com conexão segura (SSL) — confira com seu provedor de
                                        e-mail se os valores forem diferentes.
                                    </Alert>
                                </Grid>
                            </>
                        )}

                        {form.type === 'google_calendar' && (
                            <>
                                <Grid item xs={12}>
                                    <Alert severity="info" sx={{ borderRadius: '12px' }}>
                                        Não precisa cadastrar nenhuma credencial aqui - o CRM usa um único app Google já configurado pela
                                        Loged. Só administradores podem conectar contas Google e decidir quem vê os eventos de cada uma -
                                        depois de salvo este canal, use o bloco abaixo.
                                    </Alert>
                                </Grid>

                                {!isAdmin ? (
                                    <Grid item xs={12}>
                                        <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                                            Só administradores podem conectar contas Google e atribuí-las a usuários.
                                        </Alert>
                                    </Grid>
                                ) : !editingChannel ? (
                                    <Grid item xs={12}>
                                        <Alert severity="info" sx={{ borderRadius: '12px' }}>
                                            Salve o canal primeiro pra poder conectar contas Google.
                                        </Alert>
                                    </Grid>
                                ) : (
                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                                            Contas Google conectadas
                                        </Typography>

                                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                                            <TextField
                                                select
                                                size="small"
                                                label="Conectar para qual usuário"
                                                value={connectingGoogleFor}
                                                onChange={(e) => setConnectingGoogleFor(e.target.value)}
                                                sx={{ minWidth: 240 }}
                                            >
                                                <MenuItem value="">
                                                    {currentUser ? `Eu mesmo (${currentUser.first_name})` : 'Eu mesmo'}
                                                </MenuItem>
                                                {teamUsers.map((u) => (
                                                    <MenuItem key={u.id} value={u.id}>
                                                        {u.first_name} {u.last_name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>

                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={handleConnectGoogleAccount}
                                                disabled={connectingGoogle}
                                                startIcon={connectingGoogle ? <CircularProgress size={14} color="inherit" /> : null}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                {connectingGoogle ? 'Redirecionando...' : 'Conectar nova conta Google'}
                                            </Button>
                                        </Box>

                                        {loadingGoogleCalendarConnections ? (
                                            <CircularProgress size={22} />
                                        ) : googleCalendarConnections.length === 0 ? (
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                Nenhuma conta Google conectada ainda.
                                            </Typography>
                                        ) : (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                {googleCalendarConnections.map((connection) => (
                                                    <Box
                                                        key={connection.id}
                                                        sx={{
                                                            p: 2,
                                                            borderRadius: '12px',
                                                            border: '1px solid #e2e8f0',
                                                            background: '#f8fafc'
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                {connection.google_email}
                                                            </Typography>
                                                            <Chip size="small" label={`Dona: ${connection.user?.name || '—'}`} />
                                                            {connection.is_primary && <Chip size="small" color="primary" label="Padrão" />}
                                                        </Box>

                                                        <Typography
                                                            variant="caption"
                                                            sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
                                                        >
                                                            {connection.last_synced_at
                                                                ? `Última sincronização: ${new Date(connection.last_synced_at).toLocaleString('pt-BR')}`
                                                                : 'Ainda não sincronizou'}
                                                        </Typography>

                                                        {connection.synced_calendars && connection.synced_calendars.length > 0 && (
                                                            <Box
                                                                sx={{
                                                                    mt: 1.5,
                                                                    p: 1.5,
                                                                    borderRadius: '8px',
                                                                    background: '#fff',
                                                                    border: '1px solid #eef1f5'
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}
                                                                >
                                                                    Calendários que essa conta enxerga (
                                                                    {connection.synced_calendars.length})
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                                                    {connection.synced_calendars.map((sc) => (
                                                                        <Box
                                                                            key={sc.id}
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'space-between',
                                                                                gap: 1,
                                                                                flexWrap: 'wrap'
                                                                            }}
                                                                        >
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                                                <Typography variant="body2">
                                                                                    {sc.calendar_name || sc.calendar_id}
                                                                                </Typography>
                                                                                {sc.is_own_calendar && (
                                                                                    <Chip size="small" variant="outlined" label="Própria" />
                                                                                )}
                                                                                {sc.matched_user ? (
                                                                                    <Chip
                                                                                        size="small"
                                                                                        color="success"
                                                                                        variant="outlined"
                                                                                        label={`Dono: ${sc.matched_user.name}`}
                                                                                    />
                                                                                ) : (
                                                                                    <Chip
                                                                                        size="small"
                                                                                        color="warning"
                                                                                        variant="outlined"
                                                                                        label="Sem usuário correspondente — só leitura"
                                                                                    />
                                                                                )}
                                                                            </Box>
                                                                            <Switch
                                                                                size="small"
                                                                                checked={Boolean(sc.sync_enabled)}
                                                                                disabled={savingGoogleConnectionId === connection.id}
                                                                                onChange={(e) =>
                                                                                    handleToggleSyncedCalendar(connection.id, sc.id, e.target.checked)
                                                                                }
                                                                            />
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        )}

                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 2,
                                                                flexWrap: 'wrap',
                                                                mt: 1.5
                                                            }}
                                                        >
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch
                                                                        size="small"
                                                                        checked={Boolean(connection.sync_enabled)}
                                                                        disabled={savingGoogleConnectionId === connection.id}
                                                                        onChange={(e) =>
                                                                            handleToggleGoogleConnectionSync(
                                                                                connection.id,
                                                                                e.target.checked
                                                                            )
                                                                        }
                                                                    />
                                                                }
                                                                label="Sincronização ativa"
                                                            />

                                                            <Button
                                                                size="small"
                                                                color="error"
                                                                disabled={savingGoogleConnectionId === connection.id}
                                                                onClick={() => handleDisconnectGoogleConnection(connection.id)}
                                                                sx={{ textTransform: 'none' }}
                                                            >
                                                                Desconectar
                                                            </Button>
                                                        </Box>

                                                        <TextField
                                                            select
                                                            fullWidth
                                                            size="small"
                                                            label="Quem vê os eventos dessa conta na Agenda"
                                                            sx={{ mt: 1.5 }}
                                                            value={connection.assigned_users.map((u) => u.id)}
                                                            onChange={(e) => handleChangeGoogleAssignedUsers(connection.id, e.target.value)}
                                                            SelectProps={{
                                                                multiple: true,
                                                                renderValue: (selected) =>
                                                                    connection.assigned_users
                                                                        .filter((u) => selected.includes(u.id))
                                                                        .map((u) => u.name)
                                                                        .join(', ') || 'Ninguém'
                                                            }}
                                                            disabled={savingGoogleConnectionId === connection.id}
                                                        >
                                                            {teamUsers.map((u) => (
                                                                <MenuItem key={u.id} value={u.id}>
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={connection.assigned_users.some((au) => au.id === u.id)}
                                                                    />
                                                                    <ListItemText primary={`${u.first_name} ${u.last_name}`} />
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Grid>
                                )}
                            </>
                        )}

                        {form.type === 'web' && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        type="color"
                                        label="Cor do widget"
                                        value={form.widget_color}
                                        onChange={(e) => setForm({ ...form, widget_color: e.target.value })}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Domínios autorizados (opcional)"
                                        placeholder="meusite.com.br, www.meusite.com.br"
                                        helperText="Separe por vírgula. Deixe vazio para permitir qualquer site."
                                        value={form.widget_allowed_domains}
                                        onChange={(e) => setForm({ ...form, widget_allowed_domains: e.target.value })}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Mensagem de boas-vindas"
                                        placeholder="Olá! Como posso ajudar?"
                                        helperText="Aparece automaticamente assim que o visitante abre o chat."
                                        value={form.widget_welcome_message}
                                        onChange={(e) => setForm({ ...form, widget_welcome_message: e.target.value })}
                                    />
                                </Grid>

                                {!savedIntegrationId ? (
                                    <Grid item xs={12}>
                                        <Alert severity="info" sx={{ borderRadius: '12px' }}>
                                            Salve o canal para poder enviar o logo e gerar o código de instalação do widget.
                                        </Alert>
                                    </Grid>
                                ) : (
                                    <>
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                {widgetLogoKey && (
                                                    <Avatar
                                                        variant="rounded"
                                                        src={`${config.API_SERVER}v1/apps/${appId}/media/${encodePathSegments(widgetLogoKey)}/`}
                                                        sx={{ width: 48, height: 48 }}
                                                    />
                                                )}
                                                <Button
                                                    component="label"
                                                    variant="outlined"
                                                    startIcon={<IconUpload size={18} />}
                                                    disabled={uploadingLogo}
                                                    sx={{ borderRadius: '12px', textTransform: 'none' }}
                                                >
                                                    {uploadingLogo ? 'Enviando...' : 'Enviar logo'}
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        hidden
                                                        onChange={(e) => handleUploadLogo(e.target.files?.[0])}
                                                    />
                                                </Button>
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                                                Código de instalação
                                            </Typography>
                                            <Box
                                                component="pre"
                                                sx={{
                                                    p: 2,
                                                    borderRadius: '12px',
                                                    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#0f172a',
                                                    color: '#e2e8f0',
                                                    fontSize: '0.8rem',
                                                    overflowX: 'auto',
                                                    m: 0
                                                }}
                                            >
                                                {buildWidgetSnippet()}
                                            </Box>
                                            <Button
                                                onClick={handleCopyCode}
                                                startIcon={codeCopied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                                                sx={{ mt: 1, borderRadius: '12px', textTransform: 'none' }}
                                            >
                                                {codeCopied ? 'Copiado!' : 'Copiar código'}
                                            </Button>
                                        </Grid>
                                    </>
                                )}
                            </>
                        )}

                        <Grid item xs={12}>
                            <Alert severity="info" sx={{ borderRadius: '12px' }}>
                                Se sua conta já tem um agente de IA externo configurado (o mesmo que responde outros canais hoje), ele
                                também vai responder esse canal automaticamente, sem precisar de nada abaixo. Os dois campos a seguir só
                                valem pra fila humana / chatbot de fluxo criado no CRM.
                            </Alert>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Responde esse canal"
                                value={form.defaultResponderEnabled_id}
                                onChange={(e) => setForm({ ...form, defaultResponderEnabled_id: e.target.value })}
                            >
                                <MenuItem value="">Fila humana (padrão)</MenuItem>
                                {listaSwitchboardsIntegrations.map((swb) => (
                                    <MenuItem key={swb.id} value={swb.id}>
                                        {swb.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Chatbot de fluxo que responde"
                                value={form.chatbotResponder_id}
                                onChange={(e) => setForm({ ...form, chatbotResponder_id: e.target.value })}
                            >
                                <MenuItem value="">Nenhum</MenuItem>
                                {listaChatbots.map((chatbot) => (
                                    <MenuItem key={chatbot.id} value={chatbot.id}>
                                        {chatbot.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: '16px',
                                    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.05)' : '1px solid #e2e8f0'
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.status === 'active'}
                                            onChange={(e) => setForm({ ...form, status: e.target.checked ? 'active' : 'inactive' })}
                                        />
                                    }
                                    label="Canal ativo"
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
                    {editingChannel ? (
                        <Button
                            color="error"
                            startIcon={<IconTrash size={18} />}
                            onClick={() => {
                                setOpenModal(false);
                                handleDelete(editingChannel);
                            }}
                            sx={{ borderRadius: '12px', textTransform: 'none' }}
                        >
                            Excluir
                        </Button>
                    ) : (
                        <Box />
                    )}

                    <Button
                        variant="contained"
                        startIcon={<IconDeviceFloppy size={18} />}
                        onClick={handleSave}
                        disabled={saving}
                        sx={{
                            borderRadius: '14px',
                            px: 3,
                            textTransform: 'none',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg,#16a34a,#10b981)',
                            boxShadow: '0 10px 25px rgba(16,185,129,0.25)'
                        }}
                    >
                        {saving ? 'Salvando...' : 'Salvar Canal'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MessageChannels;
